import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import api, { apiAction } from '../../api'
import toPlural from '../../common/formatters/plural'
import Button from '../button'
import getActions from './actions'
import { SortUp, SortDown } from '../icons'

import Loading from '../loading'

import Item from './item'
import S from './styled'

import {
  fetchItems as fetchItemsAction,
  addItem as addItemAction,
  deleteItem as deleteItemAction,
  updateItem as updateItemAction,
} from './storeActions'

const defaultItem = { translates: {} }

const groupByTypes = (items, types) => {
  const result = {}
  items.forEach((item, i) => {
    if(result[item.typeId]) {
      result[item.typeId].list.push(item)
    } else {
      result[item.typeId] = {
        list: [item],
        name: (types.find(t => t.id === item.typeId) || { name: '' }).name,
      }
    }
  })
  return result
}

const TranslatedEntitiesEditor = props => {
  const {
    fetchLocales, locales, localesLoading,
    fetchItems, items,
    noDescription,
    withImage,
    textArea,
    imageRequired,
    noStretch,
    type,
    fetchTypes,
    types = [],
    additionalEntity,
    fetchAdditionalEntities,
    additionalEntities,
    additionalField,
  } = props

  useEffect(() => {
    (async () => {
      await fetchLocales()
      if (type) {
        try {
          await fetchTypes()
        } catch(e) {
          //eat exception
        }
      }
      if (additionalEntity) {
        try {
          await fetchAdditionalEntities(additionalEntity)
        } catch(e) {
          //eat exception
        }
      }
      await fetchItems()
    })()
  }, [type, additionalEntity]) //eslint-disable-line react-hooks/exhaustive-deps

  const [showNew, setShowNew] = useState(true)

  const [expandedGroups, setExpandedGroups] = useState([])

  const { onAdd, onDelete, onSave } = getActions({ ...props, setShowNew })

  const onCancelNew = () => setShowNew(true)

  const itemsToRender = type ? groupByTypes(items, types): items

  const handleExpandCollapseGroup = useCallback(e => {
    const groupId = parseInt(e.target.getAttribute('data-groupid'), 10)
    if (expandedGroups.includes(groupId)) {
      setExpandedGroups(expandedGroups.filter(gId => gId !== groupId))
    } else {
      setExpandedGroups([...expandedGroups, groupId])
    }
  }, [expandedGroups])

  return localesLoading ? <Loading overlay/> : locales && locales.length ? (
    <div>
      {showNew ?  (
        <Button blue onClick={() => setShowNew(false)} > Add </Button>
      ) : (
        <Item {...{
          item: defaultItem, locales, noDescription, onSave: onAdd, isNew: true, onCancelNew,
          withImage, textArea, imageRequired, noStretch,
          types,
          hasType: !!type,
          additionalEntities,
          additionalField,
        }} />
      )}
      <S.List>
        {itemsToRender instanceof Array ? itemsToRender.map((item, index) => (
          <Item {...{ item, key: item.id, locales, onDelete, noDescription, onSave,
            withImage, textArea, imageRequired, noStretch,
            types,
            hasType: !!type,
          }} />
        )) : Reflect.ownKeys(itemsToRender)
          .sort((g1,g2) => itemsToRender[g1].name.localeCompare(itemsToRender[g2].name)).map(groupIdKey => {
            const groupId = parseInt(groupIdKey, 10)
            const expanded = expandedGroups.includes(groupId)
            return (
              <S.Group key={`group_${groupId}_${expandedGroups.includes(groupId)}`}>
                <S.GroupTitle onClick={handleExpandCollapseGroup} data-groupid={groupId} className={expanded ? 'expanded' : ''}>
                  {itemsToRender[groupId].name} <S.Counter>{itemsToRender[groupId].list.length}</S.Counter>
                  <S.Collapse className="group">
                    {expanded ? <SortUp /> : <SortDown />}
                  </S.Collapse>
                </S.GroupTitle>
                {
                  expanded ? (
                    <S.GroupItems>
                      {
                        itemsToRender[groupId].list.map((item, index) => (
                          <Item {...{ item, key: item.id, locales, onDelete, noDescription, onSave,
                            withImage, textArea, imageRequired, noStretch,
                            types,
                            hasType: !!type,
                            additionalEntities,
                            additionalField,
                          }} />))
                      }
                    </S.GroupItems>
                  ) : null
                }
              </S.Group>
            )
          })}
      </S.List>
    </div>
  ) : <div>Please create at least one locale first</div>
}

TranslatedEntitiesEditor.propTypes = {
  locales: PropTypes.array,
  localesLoading: PropTypes.bool,
  fetchLocales: PropTypes.func,
  items: PropTypes.array,
}

const mapStateToProps = (state, { entityName, type, additionalEntity }) => ({
  locales: state.locales.list,
  localesLoading: state.locales.isLoading,
  items: state[toPlural(entityName)].list,
  ...(type ? { types: state[toPlural(type)].list } : null),
  ...(additionalEntity ? { additionalEntities: state[toPlural(additionalEntity)].list } : null),
})

const mapDispatchToProps = (dispatch, { entityName, type } ) => ({
  fetchLocales: () => dispatch(apiAction(api.getLocales)()),
  fetchItems: () => dispatch(fetchItemsAction(entityName)()),
  fetchTypes: () => dispatch(fetchItemsAction(type)()),
  addItem: item => dispatch(addItemAction(entityName)(item)),
  deleteItem: entityId => dispatch(deleteItemAction(entityName)(entityId)),
  updateItem: (entityId, item) => dispatch(updateItemAction(entityName)(entityId, item)),
  fetchAdditionalEntities: name => dispatch(fetchItemsAction(name)()),
})

export default connect(mapStateToProps, mapDispatchToProps)(TranslatedEntitiesEditor)
